import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

interface NewsImageUploaderProps {
  initialImageUrl?: string | null;
  initialImagePath?: string | null;
  initialImageFileName?: string | null;
  onImageUploaded: (imageUrl: string, storagePath: string, fileName: string) => void;
  onImageRemoved: () => void;
}

// Fonction utilitaire pour formater la taille du fichier
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' octets';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
  else return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
};

const NewsImageUploader: React.FC<NewsImageUploaderProps> = ({
  initialImageUrl,
  initialImagePath,
  initialImageFileName,
  onImageUploaded,
  onImageRemoved,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(initialImageUrl || null);
  const [imagePath, setImagePath] = useState<string | null | undefined>(initialImagePath || null);
  const [fileName, setFileName] = useState<string | null | undefined>(initialImageFileName || null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [compressionRatio, setCompressionRatio] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fonction pour compresser l'image avant upload
  const compressImage = async (file: File): Promise<{ blob: Blob; url: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Déterminer les dimensions maximales (max 1000px de large ou de haut)
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;
          let width = img.width;
          let height = img.height;

          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }

          // Créer un canvas pour la compression
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Impossible de créer le contexte canvas'));
            return;
          }
          
          // Dessiner l'image redimensionnée
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir en JPEG avec qualité 0.80
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Échec de la compression'));
                return;
              }
              const url = URL.createObjectURL(blob);
              resolve({ blob, url });
            },
            'image/jpeg',
            0.80
          );
        };
        img.onerror = () => reject(new Error('Échec du chargement de l\'image'));
        img.src = reader.result as string;
      };
      reader.onerror = () => reject(new Error('Échec de la lecture du fichier'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setError(null);
    setUploading(true);
    setUploadProgress(0);
    setOriginalSize(file.size);

    try {
      // Compresser l'image
      const { blob, url } = await compressImage(file);
      
      // Calculer le taux de compression
      const newCompressedSize = blob.size;
      setCompressedSize(newCompressedSize);
      if (originalSize) {
        const ratio = ((originalSize - newCompressedSize) / originalSize) * 100;
        setCompressionRatio(ratio);
      }
      
      // Générer un nom de fichier unique
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const uniqueFileName = `news_${timestamp}_${uuidv4().substring(0, 8)}.${fileExtension}`;
      
      // Chemin de stockage dans Firebase
      const storagePath = `news_images/${uniqueFileName}`;
      const storageRef = ref(storage, storagePath);
      
      // Supprimer l'ancienne image si elle existe
      if (imagePath) {
        try {
          const oldImageRef = ref(storage, imagePath);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.error("Erreur lors de la suppression de l'ancienne image:", error);
        }
      }
      
      // Uploader la nouvelle image
      const uploadTask = uploadBytesResumable(storageRef, blob);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          setError("Erreur lors de l'upload de l'image");
          setUploading(false);
          console.error("Erreur d'upload:", error);
        },
        async () => {
          // Upload terminé avec succès
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          setImageUrl(downloadUrl);
          setImagePath(storagePath);
          setFileName(uniqueFileName);
          onImageUploaded(downloadUrl, storagePath, uniqueFileName);
          setUploading(false);
          
          // Réinitialiser l'input file
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      );
    } catch (error) {
      console.error("Erreur lors du traitement de l'image:", error);
      setError("Erreur lors du traitement de l'image");
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!imagePath) return;
    
    try {
      setUploading(true);
      
      try {
        // Essayer de supprimer l'image, mais ne pas bloquer en cas d'échec
        const imageRef = ref(storage, imagePath);
        await deleteObject(imageRef);
        console.log(`Image supprimée avec succès: ${imagePath}`);
      } catch (storageError) {
        // Capturer l'erreur mais continuer le processus
        console.warn("L'image n'a pas pu être supprimée du stockage, mais nous continuons:", storageError);
        // Ne pas définir d'erreur visible pour l'utilisateur ici
      }
      
      // Toujours mettre à jour l'état local et informer le parent, même si la suppression a échoué
      setImageUrl(null);
      setImagePath(null);
      setFileName(null);
      onImageRemoved();
      
    } catch (error) {
      console.error("Erreur lors de la suppression de l'image:", error);
      setError("Erreur lors de la suppression de l'image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Image de l'actualité
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {imageUrl ? (
        <Box sx={{ mb: 2 }}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 1, 
              position: 'relative',
              overflow: 'hidden',
              '&:hover .overlay': {
                opacity: 1
              }
            }}
          >
            <Box 
              component="img" 
              src={imageUrl} 
              alt="Image de l'actualité" 
              sx={{ 
                width: '100%', 
                maxHeight: '300px', 
                objectFit: 'contain',
                display: 'block'
              }} 
            />
            
            <Box 
              className="overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              <IconButton 
                color="error" 
                onClick={handleRemoveImage}
                disabled={uploading}
                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {fileName}
            </Typography>
            
            {originalSize && compressedSize && (
              <Typography variant="caption" color="text.secondary" display="block">
                Taille originale: {formatFileSize(originalSize)} → Compressée: {formatFileSize(compressedSize)}
                {compressionRatio !== null && (
                  <span> ({compressionRatio.toFixed(1)}% de réduction)</span>
                )}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box 
          sx={{ 
            border: '2px dashed #4a90e2', 
            borderRadius: 1, 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'rgba(74, 144, 226, 0.05)',
            mb: 2
          }}
        >
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 1
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: '#4a90e2', mb: 1 }} />
            
            <Typography variant="body1" gutterBottom>
              Aucune image sélectionnée
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Sélectionner une image
            </Button>
          </Box>
        </Box>
      )}
      
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {`${Math.round(uploadProgress)}% - Upload en cours...`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NewsImageUploader;
